import { useEffect, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";

import { Mascot } from "./Mascot";
import { useMascotReady } from "./useMascotReady";
import "./styles.scss";

const SESSION_KEY = "provenduz.mascot.intro.seen";

interface MascotIntroProps {
  variant?: "isp" | "mvno";
}

/**
 * One-time-per-session welcome: the mascot waves "Alô!" then lifts the curtain
 * up, revealing the page underneath. Skipped entirely for reduced-motion users
 * and on subsequent navigations in the same tab.
 */
export const MascotIntro = ({ variant = "isp" }: MascotIntroProps) => {
  const reduce = useReducedMotion();
  const ready = useMascotReady();
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (ready !== true) return;

    let seen = false;
    try {
      seen = sessionStorage.getItem(SESSION_KEY) === "1";
    } catch {
      seen = false;
    }
    if (seen || reduce) return;

    setShow(true);
    document.body.style.overflow = "hidden";

    const t = window.setTimeout(() => setShow(false), 2100);
    return () => window.clearTimeout(t);
  }, [reduce, ready]);

  const finish = () => {
    try {
      sessionStorage.setItem(SESSION_KEY, "1");
    } catch {
      /* ignore */
    }
    document.body.style.overflow = "";
  };

  return (
    <AnimatePresence onExitComplete={finish}>
      {show && (
        <motion.div
          className={`mascot-intro mascot-intro--${variant}`}
          role="dialog"
          aria-label="Boas-vindas"
          initial={{ y: 0 }}
          exit={{ y: "-100%" }}
          transition={{ duration: 0.8, ease: [0.7, 0, 0.3, 1] }}
          onClick={() => setShow(false)}
        >
          <motion.div
            className="mascot-intro__stage"
            initial={{ opacity: 0, scale: 0.8, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          >
            <motion.div
              className="mascot-intro__bubble"
              initial={{ opacity: 0, y: 10, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ delay: 0.35, duration: 0.35 }}
            >
              Alô! 👋 Bora montar seu app?
            </motion.div>

            <motion.div
              animate={{ rotate: [0, -8, 8, -8, 0] }}
              transition={{ delay: 0.3, duration: 0.9, ease: "easeInOut" }}
              style={{ transformOrigin: "60% 90%" }}
            >
              <Mascot pose="wave" size={190} bob={false} />
            </motion.div>

            <span className="mascot-intro__skip">toque para pular</span>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
